/**
 * VantageWorkbook Store
 *
 * Single source of truth for a Vantage workbook: persisted config, tree selection,
 * extensions (toolbar + column config), and condition monitor state. The evidence
 * table definition is served by react-query (see evidenceTable.queries), and the
 * derived hooks below compose it with this state. Sections: STATE, ACTIONS,
 * SELECTORS (derived), then exported hooks for React.
 */

import { createSelector } from 'reselect';
import { useTableDefinitionQuery } from '@/tableDefinitionEditor/tableDefinition.queries';
import {
  findConditionIdColumnNames,
  findConditions,
} from '@/vantage/components/evidence_table/helpers/conditionFinders';
import {
  useReasonCodeRouting,
  useRoomReasonCodesQuery,
} from '@/vantage/components/extensionConfig/reasonCodeManagement/reasonCodes.queries';
import { VantageNames, isReasonCodesExtensionName } from '@/vantage/constants';
import { addCharacteristicsForPurviewFields, isActiveExtension, isNotesEnabled } from '@/vantage/extension.utilities';
import { mergeReasonCodeExtensions, toReasonCodeVirtualColumnName } from '@/vantage/reasonCodesExtension.utilities';
import { getSelectionIdentity } from '@/vantage/selectionIdentity.utilities';
import { getTableColumns } from '@/vantage/vantageWorkbook.utilities';
import { SeeqNames } from '@seeqdev/constants';
import { type SetStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { ReasonCodeRouting } from '@/vantage/reasonCodesExtension.utilities';
import type { ExtensionSchema } from '@/vantage/types/Extension.types';
import type { SimplifiedNode } from '@/vantage/types/SimplifiedNode.types';
import type { InstanceConfig, TableColumns, TableConfig } from '@/vantage/types/TableConfig.types';
import type { ConditionMonitorRunRange, StoppedConditionMonitorState } from '@/vantage/types/VantageWorkbook.types';

// ========================================
// STATE
// ========================================

/** User-applied context time range override. Times are epoch ms because store state is deep-frozen. */
export interface ManualContextTimeRange {
  startMs: number;
  endMs: number;
  adjustedContextKind: string;
  fromQuickTrend: boolean;
}

export interface VantageWorkbookState {
  /** Persisted workbook config from backend, drives context fetch and column derivation */
  workbookConfig?: InstanceConfig;
  /** Bumped when user edits context so evidence context is refetched */
  contextLastModified: number;
  /** Current tree selection (drives active context and which condition IDs are in scope) */
  selectedNodes: SimplifiedNode[];
  /** Staged context time range override, cleared when the logical selection changes */
  manualContextTimeRange?: ManualContextTimeRange;
  /** Extension names that are expanded or pinned in the toolbar */
  activeExtensions: string[];
  /** Condition monitor job progress percentage (0 to 1) */
  conditionMonitorJobPercentComplete?: number;
  /** Whether a user-initiated condition monitor run is waiting for the job-status channel's STARTED or FINISHED */
  conditionMonitorJobRunStarting: boolean;
  /** Whether the job-status channel says the room's condition monitor job is running */
  conditionMonitorJobLive: boolean;
  /** Run id of the condition monitor job's latest FINISHED, so the Evidence table can refetch once per run */
  conditionMonitorLastFinishedRunId?: string;
  /** Whether the job-status channel says the room's item finder job is running */
  itemFinderJobLive: boolean;
  /** Whether user has write permission on the condition monitor (for run actions) */
  conditionMonitorWritePermission?: boolean;
  /** Configured range used for manual condition monitor runs */
  conditionMonitorRunRange?: ConditionMonitorRunRange;
  conditionMonitorLastRunAt?: string;
  /** Snapshot when monitor was stopped (enabled/archived, job id) for resume and stopped-state UI */
  stoppedConditionMonitorState?: StoppedConditionMonitorState;
  /** Condition IDs that pass the evidence table filters, synced from the table */
  filteredConditionIds: string[];
}

const initialState: VantageWorkbookState = {
  workbookConfig: undefined,
  contextLastModified: 0,
  selectedNodes: [],
  manualContextTimeRange: undefined,
  activeExtensions: [],
  conditionMonitorJobPercentComplete: undefined,
  conditionMonitorJobRunStarting: false,
  conditionMonitorJobLive: false,
  conditionMonitorLastFinishedRunId: undefined,
  itemFinderJobLive: false,
  conditionMonitorWritePermission: undefined,
  conditionMonitorRunRange: undefined,
  conditionMonitorLastRunAt: undefined,
  stoppedConditionMonitorState: undefined,
  filteredConditionIds: [],
};

// ========================================
// STORE
// ========================================

const vantageWorkbookActionsFactory = (set: SetStoreState<VantageWorkbookState>) => ({
  setWorkbook: (workbookConfig?: InstanceConfig) => {
    set((draft) => {
      draft.workbookConfig = workbookConfig;
    });
  },

  /**
   * Sets contextLastModified to current timestamp.
   */
  markContextModified: () => {
    set((draft) => {
      draft.contextLastModified = Date.now();
    });
  },

  /**
   * Set extensions in workbook config.
   * Filters to only active extensions.
   */
  setExtensions: (extensions: ExtensionSchema[]) => {
    set((draft) => {
      if (draft.workbookConfig) {
        draft.workbookConfig.extensions = extensions.filter(isActiveExtension);
      }
    });
  },

  setActiveExtensions: (activeExtensions: string[]) => {
    set((draft) => {
      draft.activeExtensions = activeExtensions;
    });
  },

  setSelectedNodes: (selectedNodes: SimplifiedNode[]) => {
    set((draft) => {
      if (getSelectionIdentity(draft.selectedNodes) !== getSelectionIdentity(selectedNodes)) {
        draft.manualContextTimeRange = undefined;
      }
      draft.selectedNodes = selectedNodes;
    });
  },

  setManualContextTimeRange: (manualContextTimeRange: ManualContextTimeRange | undefined) => {
    set((draft) => {
      draft.manualContextTimeRange = manualContextTimeRange;
    });
  },

  setConditionMonitorJobPercentComplete: (percentComplete: number | undefined) => {
    set((draft) => {
      draft.conditionMonitorJobPercentComplete = percentComplete;
    });
  },

  setConditionMonitorJobRunStarting: (isStarting: boolean) => {
    set((draft) => {
      draft.conditionMonitorJobRunStarting = isStarting;
    });
  },

  setConditionMonitorJobLive: (isLive: boolean) => {
    set((draft) => {
      draft.conditionMonitorJobLive = isLive;
    });
  },

  setConditionMonitorLastFinishedRunId: (runId: string | undefined) => {
    set((draft) => {
      draft.conditionMonitorLastFinishedRunId = runId;
    });
  },

  setItemFinderJobLive: (isLive: boolean) => {
    set((draft) => {
      draft.itemFinderJobLive = isLive;
    });
  },

  setConditionMonitorWritePermission: (hasWritePermission: boolean | undefined) => {
    set((draft) => {
      draft.conditionMonitorWritePermission = hasWritePermission;
    });
  },

  setConditionMonitorRunRange: (runRange: ConditionMonitorRunRange | undefined) => {
    set((draft) => {
      draft.conditionMonitorRunRange = runRange;
    });
  },

  setConditionMonitorLastRunAt: (lastRunAt: string | undefined) => {
    set((draft) => {
      draft.conditionMonitorLastRunAt = lastRunAt;
    });
  },

  setIncludeExtinct: (includeExtinct: boolean) => {
    set((draft) => {
      if (draft.workbookConfig) {
        draft.workbookConfig.includeExtinct = includeExtinct;
      }
    });
  },

  setIsManualEventsEnabled: (isManualEventsEnabled: boolean) => {
    set((draft) => {
      if (draft.workbookConfig) {
        draft.workbookConfig.isManualEventsEnabled = isManualEventsEnabled;
      }
    });
  },

  setIsReasonCodeButtonPerHierarchy: (isReasonCodeButtonPerHierarchy: boolean) => {
    set((draft) => {
      if (draft.workbookConfig) {
        draft.workbookConfig.isReasonCodeButtonPerHierarchy = isReasonCodeButtonPerHierarchy;
      }
    });
  },

  setStoppedConditionMonitorState: (stoppedConditionMonitorState: StoppedConditionMonitorState | undefined) => {
    set((draft) => {
      draft.stoppedConditionMonitorState = stoppedConditionMonitorState;
    });
  },

  setFilteredConditionIds: (filteredConditionIds: string[]) => {
    set((draft) => {
      draft.filteredConditionIds = filteredConditionIds;
    });
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set(() => initialState);
  },
});

const [VantageWorkbookStoreProvider, useVantageWorkbookStore, useVantageWorkbookSelector] = createStore(
  'VantageWorkbook',
  initialState,
  vantageWorkbookActionsFactory,
);

/**
 * Creates a new store instance for tests that must assert exact store state or when
 * porting Flux store tests that call actions imperatively.
 *
 * Prefer `<TestStoreProvider>` or `<*StoreProvider>` in tests and validate behavior
 * through UI interactions + DOM assertions whenever possible.
 *
 * Use with `<TestStoreProvider>` and control state via `setState`.
 *
 * @example
 * ```tsx
 * const store = createTestVantageWorkbookStore();
 * render(<TestStoreProvider vantageWorkbookStore={store}><MyComponent /></TestStoreProvider>);
 * act(() => store.setState({ workbookConfig: mockConfig }));
 * ```
 *
 * NOTE: Only use this function in **test files**.
 *
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestVantageWorkbookStore(overrides?: Partial<VantageWorkbookState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestVantageWorkbookStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, vantageWorkbookActionsFactory);
}

// ========================================
// EXPORTS: Provider
// ========================================

export { VantageWorkbookStoreProvider };

// ========================================
// SELECTORS
// ========================================

/**
 * Workbook extensions with Notes augmented by tableConfig purview fields (extra context characteristics)
 */
const selectExtensions = createSelector(
  (state: VantageWorkbookState) => state.workbookConfig?.extensions,
  (state: VantageWorkbookState) => state.workbookConfig?.tableConfig?.purviewFields,
  (extensions, purviewFields): ExtensionSchema[] => {
    if (!extensions) return [];

    return extensions.map((ext) =>
      ext.name === VantageNames.Extensions.Notes ? addCharacteristicsForPurviewFields(ext, purviewFields ?? []) : ext,
    );
  },
);

const reasonCodeColumns = (extensions: ExtensionSchema[]) =>
  extensions
    .filter((extension) => isReasonCodesExtensionName(extension.name))
    .flatMap((extension) => extension.uiConfig?.columns ?? []);

// Simple selectors (no memoization needed for primitives/direct references)
const selectTableConfig = (state: VantageWorkbookState): TableConfig | undefined => state.workbookConfig?.tableConfig;
const selectTableIds = (state: VantageWorkbookState): Record<string, string> | undefined =>
  state.workbookConfig?.tableIds;
const selectWorkbookId = (state: VantageWorkbookState): string | undefined => state.workbookConfig?.workbookId;

/**
 * Whether to include extinct capsules. Defaults true until config loads (some workbooks lack "Last Capsule State").
 */
const selectIncludeExtinct = (state: VantageWorkbookState): boolean => state.workbookConfig?.includeExtinct ?? true;
const selectIsManualEventsEnabled = (state: VantageWorkbookState): boolean =>
  state.workbookConfig?.isManualEventsEnabled ?? false;
const selectIsReasonCodeButtonPerHierarchy = (state: VantageWorkbookState): boolean =>
  state.workbookConfig?.isReasonCodeButtonPerHierarchy ?? true;

// ========================================
// EXPORTS: State Hooks (Fine-Grained Selectors)
// ========================================

/**
 * Store actions (stable reference, does not trigger re-renders)
 */
export const useVantageWorkbookActions = () => useVantageWorkbookStore().actions;

/** Persisted workbook config from backend */
export const useWorkbookConfig = () => useVantageWorkbookSelector((state) => state.workbookConfig);

/**
 * Extensions with Notes + purview-derived context characteristics, plus the reason code buttons for the
 * hierarchies assigned in the room: one reason_codes extension covering them all, or one reason_codes::<id>
 * extension per hierarchy when the room shows a button per hierarchy. The assigned hierarchies live in the
 * room query (react-query), not the store, so the derivation composes both here.
 */
export const useExtensions = (): ExtensionSchema[] => {
  const persistedExtensions = useVantageWorkbookSelector(selectExtensions);
  const legacyReasonCodeTableId = useVantageWorkbookSelector((state) => state.workbookConfig?.tableIds?.reasonCodes);
  const isButtonPerHierarchy = useVantageWorkbookSelector(selectIsReasonCodeButtonPerHierarchy);
  const workbookId = useWorkbookId();
  const { data: roomReasonCodes } = useRoomReasonCodesQuery(workbookId);
  return mergeReasonCodeExtensions(
    persistedExtensions,
    legacyReasonCodeTableId,
    roomReasonCodes?.hierarchies,
    isButtonPerHierarchy,
  );
};

/** Condition routing that attributes reason codes to their hierarchy's button; undefined when one button serves all. */
export const useReasonCodeButtonRouting = (): ReasonCodeRouting | undefined => {
  const { routing } = useReasonCodeRouting(useWorkbookId());
  return useVantageWorkbookSelector(selectIsReasonCodeButtonPerHierarchy) ? routing : undefined;
};

/** Whether users can type new notes in this room */
export const useIsNotesEnabled = () =>
  useVantageWorkbookSelector((state) => isNotesEnabled(state.workbookConfig?.extensions ?? []));

/** Map of logical table names to backend table IDs */
export const useTableIds = () => useVantageWorkbookSelector(selectTableIds);

/** Base + extension column config (drives evidence table columns) */
export const useTableColumns = (): TableColumns | undefined => {
  const extensions = useExtensions();
  const evidenceTableDefinition = useEvidenceTableDefinition();
  if (!evidenceTableDefinition) return undefined;
  return getTableColumns(evidenceTableDefinition.columnDefinitions, extensions);
};

/** Condition reference column names derived from tableColumns */
export const useConditionsList = (): string[] | undefined => {
  const tableColumns = useTableColumns();
  if (!tableColumns) return undefined;
  return findConditions(tableColumns, findConditionIdColumnNames(tableColumns));
};

/** Backend evidence table definition (structure + condition monitor config) */
export const useEvidenceTableDefinition = () => useTableDefinitionQuery(useTableIds()?.evidence).data;

/** Condition IDs currently passing evidence table filters */
export const useFilteredConditionIds = () => useVantageWorkbookSelector((state) => state.filteredConditionIds);

/** Condition monitor job progress in [0, 1] */
export const useConditionMonitorJobPercentComplete = () =>
  useVantageWorkbookSelector((state) => state.conditionMonitorJobPercentComplete);

/** Whether a user-initiated condition monitor run is waiting for the job-status channel's STARTED or FINISHED */
export const useConditionMonitorJobRunStarting = () =>
  useVantageWorkbookSelector((state) => state.conditionMonitorJobRunStarting);

/** Whether the room's condition monitor job is running, per the job-status channel */
export const useConditionMonitorJobLive = () => useVantageWorkbookSelector((state) => state.conditionMonitorJobLive);

/** Run id of the room's latest finished condition monitor job */
export const useConditionMonitorLastFinishedRunId = () =>
  useVantageWorkbookSelector((state) => state.conditionMonitorLastFinishedRunId);

/** Whether the room's item finder job is running, per the job-status channel */
export const useItemFinderJobLive = () => useVantageWorkbookSelector((state) => state.itemFinderJobLive);

/** Whether user has write permission on the condition monitor */
export const useConditionMonitorWritePermission = () =>
  useVantageWorkbookSelector((state) => state.conditionMonitorWritePermission);

/** Configured range used for manual condition monitor runs */
export const useConditionMonitorRunRange = () => useVantageWorkbookSelector((state) => state.conditionMonitorRunRange);

export const useConditionMonitorLastRunAt = () =>
  useVantageWorkbookSelector((state) => state.conditionMonitorLastRunAt);

/** Last saved state when monitor was stopped (for resume and disabled/archived UI) */
export const useStoppedConditionMonitorState = () =>
  useVantageWorkbookSelector((state) => state.stoppedConditionMonitorState);

/** Currently selected nodes in the workbook tree */
export const useSelectedNodes = () => useVantageWorkbookSelector((state) => state.selectedNodes);

/** Staged context time range override */
export const useManualContextTimeRange = () => useVantageWorkbookSelector((state) => state.manualContextTimeRange);

/** Extension names expanded or pinned in the toolbar */
export const useActiveExtensions = () => useVantageWorkbookSelector((state) => state.activeExtensions);

/** Timestamp bumped when context is edited (used to refetch evidence context) */
export const useContextLastModified = () => useVantageWorkbookSelector((state) => state.contextLastModified);

/** Table config (purview fields, date range, context mappings) */
export const useTableConfig = () => useVantageWorkbookSelector(selectTableConfig);

/** Workbook ID from persisted config */
export const useWorkbookId = () => useVantageWorkbookSelector(selectWorkbookId);

/** Include extinct capsules (default true until config loads) */
export const useIncludeExtinct = () => useVantageWorkbookSelector(selectIncludeExtinct);

/** Whether manual events are enabled for the workbook */
export const useIsManualEventsEnabled = () => useVantageWorkbookSelector(selectIsManualEventsEnabled);

export const useIsReasonCodeButtonPerHierarchy = () => useVantageWorkbookSelector(selectIsReasonCodeButtonPerHierarchy);

/** User-defined context extension names (for context column pickers) */
export const useUserDefinedContextColumnNames = (): string[] => {
  const extensions = useExtensions();
  return extensions
    .filter((extension) => extension.name.startsWith(SeeqNames.SeeqContextNameSpace.UserDefinedContextNamespace))
    .map((extension) => extension.name);
};

/**
 * Reason code virtual column names, all prefixed with "reasonCode." so the backend can
 * identify them. The "Author" column uses "reasonCode.author"; label columns use
 * "reasonCode.{category}". The backend strips the prefix for label lookups and uses
 * the sibling categories for the author creator lookup.
 */
export const useReasonCodeColumnNames = (): string[] => {
  const extensions = useExtensions();
  // Deduped union across all reason code extensions (hierarchies can share level and author columns).
  return [
    ...new Set(
      reasonCodeColumns(extensions)
        .filter((column) => column.category)
        .map((column) => toReasonCodeVirtualColumnName(column.colDefName ?? '', column.category!)),
    ),
  ];
};

/**
 * Maps reason code column display names (colDefName) to their SSRM virtual column names.
 * Used to resolve sort/filter/groupBy column names to the backend virtual column identifiers.
 */
export const useReasonCodeColumnNameMap = (): Record<string, string> => {
  const extensions = useExtensions();
  return Object.fromEntries(
    reasonCodeColumns(extensions)
      .filter((column) => column.colDefName && column.category)
      .map((column) => [column.colDefName!, toReasonCodeVirtualColumnName(column.colDefName!, column.category!)]),
  );
};
